import React, { useRef, useState } from "react";
import { Button } from "@mui/material";
import { UploadAudio } from "../utils/uploadMedia";
import { showError, showToast } from "../constants/toasts";

interface AudioUploadProps {
  onUploadSuccess: (audioData: any) => void;
  onUploadError?: (error: string) => void;
  disabled?: boolean;
  className?: string;
}

const AudioUpload: React.FC<AudioUploadProps> = ({
  onUploadSuccess,
  onUploadError,
  disabled = false,
  className = "",
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const handleFileSelect = async (file: File) => {
    // Validate file type
    if (!file.type.startsWith("audio/")) {
      showError("Please select an audio file");
      return;
    }

    // Validate file size (e.g., max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      showError("File size must be less than 10MB");
      return;
    }

    setUploadedFile(file);
    setIsUploading(true);

    try {
      const response = await UploadAudio(file);
      
      if (response.status === 200 && response.data && response.data.length > 0) {
        const audioData = response.data[0];
        onUploadSuccess(audioData);
        showToast("Audio uploaded successfully");
      } else {
        throw new Error(response.message || "Upload failed");
      }
    } catch (error: any) {
      const errorMessage = error?.message || "Failed to upload audio file";
      showError(errorMessage);
      onUploadError?.(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (disabled || isUploading) return;

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={`audio-upload-container ${className}`}>
      <div
        className={`audio-upload-area ${isUploading ? "uploading" : ""} ${disabled ? "disabled" : ""}`}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        style={{
          border: "2px dashed #e0e0e0",
          borderRadius: "8px",
          padding: "40px 20px",
          textAlign: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          backgroundColor: disabled ? "#f5f5f5" : "#fafafa",
          transition: "all 0.3s ease",
          minHeight: "120px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            fontSize: "24px",
            color: "#1976d2",
            marginBottom: "12px",
          }}
        >
          🎵
        </div>
        <h3
          style={{
            margin: "0 0 8px 0",
            color: "#333",
            fontSize: "16px",
            fontWeight: "500",
          }}
        >
          {isUploading ? "Uploading..." : "Upload Audio"}
        </h3>
        <p
          style={{
            margin: "0",
            color: "#666",
            fontSize: "14px",
          }}
        >
          {isUploading
            ? "Please wait while your audio is being uploaded..."
            : "Click to select or drag and drop an audio file"}
        </p>
        {uploadedFile && (
          <p
            style={{
              margin: "8px 0 0 0",
              color: "#1976d2",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            Selected: {uploadedFile.name}
          </p>
        )}
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        onChange={handleFileInputChange}
        style={{ display: "none" }}
        disabled={disabled || isUploading}
      />
    </div>
  );
};

export default AudioUpload;
